import { ENTER } from '@angular/cdk/keycodes'
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { MatChipInputEvent } from '@angular/material/chips'
import { ApiService, Estimate, ModelClaimSearchResult } from '@mmx/shared'
import { get } from 'lodash'
import { BehaviorSubject, catchError, EMPTY, filter, finalize, Subject, switchMap, takeUntil, tap } from 'rxjs'

@Component({
  selector: 'mmx-model-claim-search',
  templateUrl: './model-claim-search.component.html',
  standalone: false,
})
export class ModelClaimSearchComponent implements OnInit, OnDestroy, OnChanges {
  @Input() facilityId: string
  @Input() serviceType: Estimate.PATIENT_TYPE
  @Input() physicianId: string | null = null
  @Input() prefillQuery: string | null = null
  @Input() estimateId: string | null = null
  @Input() showTraceButton = false
  @Input() traceButtonDisabled = false
  @Output() modelClaim = new EventEmitter<ModelClaimSearchResult>()
  @Output() traceRequested = new EventEmitter<void>()

  loading$ = new BehaviorSubject(false)
  search$ = new BehaviorSubject<string>(null)
  errorMessages$ = new BehaviorSubject<string[]>([])
  noResults$ = new BehaviorSubject(false)
  readonly separatorKeysCodes = [ENTER]
  chips: string[] = []

  private onDestroy$ = new Subject<void>()

  constructor(
    private restService: ApiService,
    private fb: FormBuilder,
  ) { }

  claimForm = this.fb.group({
    chipInput: this.fb.control<string>(null),
  })

  modelClaimSearch$ = this.search$.pipe(
    takeUntil(this.onDestroy$),
    filter(Boolean),
    switchMap(query => this.restService.getModelClaim({
      facilityId: this.facilityId,
      serviceType: this.serviceType,
      query,
      physicianId: this.physicianId ?? undefined,
      estimateId: this.estimateId ?? undefined,
    }).pipe(
      finalize(() => this.loading$.next(false)),
      catchError((err: any) => {
        this.errorMessages$.next(get(err, 'error.error.message', ''))
        this.claimForm.controls.chipInput.setErrors({ invalid: true })
        return EMPTY
      }),
    )),
    // A successful response with no charges means the payer/claim search found nothing. Still emit so the
    // trace reaches the handlers (which guard the charge replacement) and flag the no-results banner.
    tap(claim => {
      this.noResults$.next((claim?.charges?.length ?? 0) === 0)
      this.modelClaim.next(claim)
    }),
  )

  ngOnInit(): void {
    this.modelClaimSearch$.subscribe()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.prefillQuery) {
      const query = changes.prefillQuery.currentValue ?? null
      const nextChips = query
        ? query
          .split('|')
          .map((value) => value.trim())
          .filter(Boolean)
        : []
      this.chips = nextChips
      this.claimForm.controls.chipInput.setValue(null)
      this.resetFeedback()
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  onSubmit(): void {
    if (this.chips.length === 0) {
      return
    }
    // Clear any error left by a prior request so a new search starts from a clean state.
    this.resetFeedback()
    this.loading$.next(true)
    this.search$.next(this.chips.join('|'))
  }

  private resetFeedback(): void {
    this.claimForm.controls.chipInput.setErrors(null)
    this.errorMessages$.next([])
    this.noResults$.next(false)
  }

  onTraceRequested(): void {
    if (this.traceButtonDisabled) {
      return
    }
    this.traceRequested.emit()
  }

  addChip(event: MatChipInputEvent): void {
    const value = (event.value ?? '').trim()
    if (value) {
      this.chips.push(value)
      // The banner reflects the query that was searched; a changed chip set no longer matches it.
      this.resetFeedback()
    }
    event.chipInput?.clear()
    this.claimForm.controls.chipInput.setValue(null)
  }

  removeChip(chip: string): void {
    this.chips = this.chips.filter((value) => value !== chip)
    this.resetFeedback()
  }
}
